/**
 * Aussehen des Hauptbilds: Hintergrund hinter dem Kamerabild, Bildkorrektur
 * je Kamera, Freistellen der Regie-Kamera.
 *
 * Alles hier ist bewusst so gebaut, dass es sich in *eine* CSS-Regel am
 * `<video>` uebersetzen laesst. Der Grund ist der RTMP-Ausgang: der Server
 * nimmt die fertig gesetzte Zuschauerseite auf, also landet jede CSS-Wirkung
 * automatisch im YouTube-Stream mit -- ohne dass irgendwo ein zweites Mal
 * gerechnet werden muss und ohne dass die sendenden Handys etwas davon
 * merken. Was nicht in CSS geht, ist genau eine Sache: das Freistellen. Das
 * braucht eine Maske pro Bild und passiert deshalb beim Sender.
 */

/* ----------------------------------------------------- Hintergrund (Bühne) */

/**
 * Woraus der Hintergrund entsteht.
 * `none` laesst es schwarz wie vorher, `cover` nimmt das Titelbild des Events,
 * `accent` einen Verlauf aus seiner Farbe, `image` ein eigenes Bild.
 */
export type BackdropMode = 'none' | 'cover' | 'accent' | 'image';

export const BACKDROP_MODES: { id: BackdropMode; label: string; hint: string }[] = [
  { id: 'none', label: 'Schwarz', hint: 'Kein Hintergrund — wie bisher.' },
  { id: 'cover', label: 'Titelbild', hint: 'Das Titelbild des Events, weichgezeichnet.' },
  { id: 'accent', label: 'Vereinsfarbe', hint: 'Ruhiger Verlauf aus der Event-Farbe.' },
  { id: 'image', label: 'Eigenes Bild', hint: 'Extra hochgeladener Hintergrund (Einstellungen).' },
];

/**
 * Wie das Kamerabild im 16:9-Rahmen sitzt. `contain` zeigt es vollstaendig und
 * laesst daneben den Hintergrund sehen -- genau dafuer gibt es ihn. `cover`
 * fuellt den Rahmen und beschneidet dafuer.
 */
export type StageFit = 'contain' | 'cover';

export interface StageLook {
  backdrop: BackdropMode;
  fit: StageFit;
  /** Weichzeichnung des Hintergrunds in Pixeln. 0 = scharf. */
  blur: number;
  /** Abdunkeln des Hintergrunds in Prozent -- damit das Bild vorne bleibt. */
  dim: number;
}

export const defaultStage = (): StageLook => ({
  backdrop: 'none',
  fit: 'contain',
  // Ein Foto braucht beides: Unschaerfe, damit es nicht mit dem Kamerabild um
  // Aufmerksamkeit streitet, und etwas Schwarz darueber, damit die
  // Einblendungen lesbar bleiben.
  blur: 22,
  dim: 35,
});

export const MAX_BACKDROP_BLUR = 40;
export const MAX_BACKDROP_DIM = 85;

/* -------------------------------------------------------- Bild pro Kamera */

/**
 * `custom` ist keine eigene Einstellung, sondern die Merkung „hier hat die
 * Regie selbst an den Reglern gedreht" -- damit die Oberflaeche keine Kachel
 * hervorhebt, die nicht mehr passt.
 */
export type LookPresetId = 'neutral' | 'aufhellen' | 'warm' | 'kuehl' | 'kraeftig' | 'sw' | 'custom';

export interface CameraLook {
  preset: LookPresetId;
  /** Prozent, 100 = unveraendert. */
  brightness: number;
  contrast: number;
  saturation: number;
  /** Farbstimmung: negativ kuehl, positiv warm. */
  warmth: number;
  /** Bild spiegeln -- fuer Frontkameras, die verkehrt herum wirken. */
  mirror: boolean;
}

export const defaultCameraLook = (): CameraLook => ({
  preset: 'neutral',
  brightness: 100,
  contrast: 100,
  saturation: 100,
  warmth: 0,
  mirror: false,
});

/** Die Voreinstellungen. `mirror` bleibt bewusst aussen vor: das ist eine
 *  Eigenschaft der Kamera, keine des Looks. */
export const LOOK_PRESETS: {
  id: Exclude<LookPresetId, 'custom'>;
  label: string;
  values: Omit<CameraLook, 'preset' | 'mirror'>;
}[] = [
  { id: 'neutral', label: 'Neutral', values: { brightness: 100, contrast: 100, saturation: 100, warmth: 0 } },
  { id: 'aufhellen', label: 'Aufhellen', values: { brightness: 118, contrast: 104, saturation: 104, warmth: 4 } },
  { id: 'warm', label: 'Warm', values: { brightness: 104, contrast: 102, saturation: 112, warmth: 34 } },
  { id: 'kuehl', label: 'Kühl', values: { brightness: 102, contrast: 104, saturation: 104, warmth: -32 } },
  { id: 'kraeftig', label: 'Kräftig', values: { brightness: 102, contrast: 118, saturation: 132, warmth: 6 } },
  { id: 'sw', label: 'Schwarzweiß', values: { brightness: 104, contrast: 114, saturation: 0, warmth: 0 } },
];

export const lookPreset = (id: LookPresetId) => LOOK_PRESETS.find((p) => p.id === id) ?? null;

/** Wendet eine Voreinstellung an und laesst `mirror` unberuehrt. */
export function applyPreset(look: CameraLook, id: Exclude<LookPresetId, 'custom'>): CameraLook {
  const preset = lookPreset(id);
  if (!preset) return look;
  return { ...look, ...preset.values, preset: id };
}

export const LOOK_LIMITS = {
  brightness: [40, 160],
  contrast: [40, 160],
  saturation: [0, 200],
  warmth: [-60, 60],
} as const;

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Die CSS-`filter`-Kette einer Kamera. Genau diese Zeichenkette benutzen
 * Zuschauersicht, Regie-Monitor und die Aufnahmeseite -- deshalb steht sie
 * hier und nicht in einer Komponente.
 *
 * Die Farbstimmung ist eine Annaeherung, keine Farbtemperatur: warm laeuft
 * ueber `sepia` (schiebt ins Bernsteinfarbene) mit gegengerechneter
 * Saettigung, kuehl ueber eine kleine Drehung des Farbkreises. Echtes
 * Weissabgleich-Rechnen braeuchte eine Farbmatrix -- die gibt es in CSS nur
 * ueber einen SVG-Filter, und der wuerde bei jedem Zuschauer ein eigenes
 * Filterelement verlangen. Fuer „zu blau" oder „zu kalt" reicht das hier.
 */
export function lookFilter(look: CameraLook): string {
  const parts: string[] = [];
  if (look.brightness !== 100) parts.push(`brightness(${round2(look.brightness / 100)})`);
  if (look.contrast !== 100) parts.push(`contrast(${round2(look.contrast / 100)})`);

  if (look.warmth > 0) {
    parts.push(`sepia(${round2(look.warmth * 0.006)})`);
    // Sepia zieht Farbe heraus -- das hier holt sie zurueck.
    parts.push(`saturate(${round2((look.saturation / 100) * (1 + look.warmth * 0.006))})`);
  } else if (look.warmth < 0) {
    parts.push(`hue-rotate(${round2(look.warmth * 0.12)}deg)`);
    if (look.saturation !== 100) parts.push(`saturate(${round2(look.saturation / 100)})`);
  } else if (look.saturation !== 100) {
    parts.push(`saturate(${round2(look.saturation / 100)})`);
  }

  return parts.length ? parts.join(' ') : 'none';
}

export const lookTransform = (look: CameraLook): string => (look.mirror ? 'scaleX(-1)' : 'none');

/* ---------------------------------------------------------- Freistellen */

/**
 * Was mit dem Hintergrund der Regie-Kamera passiert.
 *
 * Nur die Regie, und das ist eine bewusste Grenze: die Maske entsteht pro
 * Bild in einem KI-Modell im Browser. Auf einem Laptop kostet das wenig, auf
 * dem Handy einer Athletin waere es Bildrate und Akku -- und genau dieses
 * Handy sendet gleichzeitig per WebRTC.
 */
export type SegmentMode = 'off' | 'blur' | 'backdrop';

export const SEGMENT_MODES: { id: SegmentMode; label: string; hint: string }[] = [
  { id: 'off', label: 'Aus', hint: 'Kamerabild unverändert senden.' },
  { id: 'blur', label: 'Weichzeichnen', hint: 'Echter Hintergrund bleibt, wird aber unscharf.' },
  { id: 'backdrop', label: 'Ersetzen', hint: 'Hintergrund wird durch den Bühnen-Hintergrund ersetzt.' },
];

/**
 * Das Freistellen wird beim Sender ins Bild gerechnet, weil eine Tonspur
 * Alpha kann, eine WebRTC-Videospur aber nicht: Transparenz uebersteht die
 * Uebertragung nicht. Der Ersatzhintergrund muss also *vor* dem Senden im
 * Bild sein. Praktischer Nebeneffekt: Zuschauer und RTMP-Ausgang bekommen das
 * Ergebnis ohne jeden Zusatzaufwand.
 */
export const SEGMENT_RUNS_AT_SENDER = true;
